import React, { useRef, useState, useEffect } from "react";

declare global {
  interface Window {
    google: any;
  }
}

export interface AddressDetails {
  formatted: string;
  streetNumber?: string;
  streetName?: string;
  street?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  lat?: number;
  lng?: number;
}

interface Props {
  value: string;
  onChange: (v: string) => void;
  onPlaceSelect?: (d: AddressDetails) => void;
  placeholder?: string;
  countryBias?: string;
  locationBias?: { lat: number; lng: number; radius?: number }; // Office location to prioritize nearby results
  lat?: number; // Stored latitude (from DB)
  lng?: number; // Stored longitude (from DB)
  className?: string;
  id?: string;
  name?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  required?: boolean;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
}

// -------------------------
// Load Google script (prevent duplicate loading)
// -------------------------
let loaded = false;
let loadingPromise: Promise<void> | null = null;

const loadGoogle = (key: string) =>
  new Promise<void>((resolve, reject) => {
    // If already loaded, resolve immediately
    if (loaded && window.google?.maps) {
      return resolve();
    }

    // If currently loading, wait for existing promise
    if (loadingPromise) {
      return loadingPromise.then(resolve).catch(reject);
    }

    // Check if script already exists in DOM
    const existingScript = document.querySelector(`script[src*="maps.googleapis.com"]`);
    if (existingScript) {
      // Wait for it to load
      const checkLoaded = setInterval(() => {
        if (window.google?.maps) {
          clearInterval(checkLoaded);
          loaded = true;
          loadingPromise = null;
          resolve();
        }
      }, 100);

      setTimeout(() => {
        clearInterval(checkLoaded);
        if (!window.google?.maps) {
          loadingPromise = null;
          reject(new Error("Google Maps script failed to load"));
        }
      }, 10000);
      return;
    }

    // Create new script
    loadingPromise = new Promise<void>((loadResolve, loadReject) => {
      const s = document.createElement("script");
      s.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places&loading=async`;
      s.async = true;
      s.defer = true;
      s.onload = () => {
        loaded = true;
        loadingPromise = null;
        loadResolve();
      };
      s.onerror = () => {
        loadingPromise = null;
        loadReject(new Error("Failed to load Google Maps script"));
      };
      document.head.appendChild(s);
    });

    loadingPromise.then(resolve).catch(reject);
  });

export const GoogleAddressAutocomplete = ({
  value,
  onChange,
  onPlaceSelect,
  placeholder = "Search address…",
  countryBias,
  locationBias,
  lat,
  lng,
  className = "",
  id,
  name,
  disabled = false,
  autoFocus = false,
  required = false,
  onBlur,
}: Props) => {
  const [predictions, setPredictions] = useState<any[]>([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  // Initialize with stored coordinates if available
  const [selectedLocation, setSelectedLocation] = useState<{ lat: number; lng: number } | null>(
    lat && lng ? { lat, lng } : null
  );
  const [showMapModal, setShowMapModal] = useState(false);
  const [isUserTyping, setIsUserTyping] = useState(false);
  
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLUListElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);

  const geocoderRef = useRef<any>(null);
  const placesLibraryRef = useRef<any>(null);
  
  // -------------------------
  // Clear input
  // -------------------------
  const handleClear = () => {
    onChange("");
    setPredictions([]);
    setOpen(false);
    setActiveIndex(-1);
    setSelectedLocation(null);
    setIsUserTyping(false);
    inputRef.current?.focus();
  };
  

  // -------------------------
  // Load Places library (new API requires importLibrary)
  // -------------------------
  const loadPlacesLibrary = async () => {
    if (!window.google?.maps) {
      throw new Error("Google Maps not loaded");
    }

    if (placesLibraryRef.current) {
      return placesLibraryRef.current;
    }

    try {
      const library = await window.google.maps.importLibrary("places");
      placesLibraryRef.current = library;
      return library;
    } catch (error) {
      console.error("Failed to load Places library:", error);
      throw error;
    }
  };

  // -------------------------
  // Fetch predictions as the user types
  // Uses AutocompleteSuggestion (new Google Maps Places API)
  // -------------------------
  const fetchPredictions = async (input: string) => {
    if (disabled) return;
    
    if (!input.trim()) {
      setPredictions([]);
      setOpen(false);
      return;
    }

    // Only fetch predictions if user is actively typing
    if (!isUserTyping) {
      setPredictions([]);
      setOpen(false);
      return;
    }

    if (!window.google?.maps) {
      console.error("Google Maps API not loaded");
      return;
    }

    setLoading(true);
    console.log("Fetching predictions for:", input);

    try {
      // Load Places library using importLibrary
      const placesLibrary = await loadPlacesLibrary();
      const { AutocompleteSuggestion } = placesLibrary;

      const request: any = {
        input: input,
        includedRegionCodes: countryBias ? [countryBias.toLowerCase()] : undefined,
      };

      // Location bias
      if (locationBias) {
        request.locationBias = {
          circle: {
            center: {
              latitude: locationBias.lat,
              longitude: locationBias.lng,
            },
            radius: locationBias.radius || 200000,
          },
        };
      }

      const response = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
      
      setLoading(false);
      console.log("Predictions response:", response);

      if (!response || !response.suggestions) {
        setPredictions([]);
        setOpen(false);
        return;
      }

      // Convert new API format to expected format for processPredictions
      const results = response.suggestions
        .filter((suggestion: any) => suggestion.placePrediction)
        .map((suggestion: any) => {
          const prediction = suggestion.placePrediction;
          const placeId = prediction.placeId || prediction.place?.id;
          const text = prediction.text?.text || '';
          const structuredFormat = prediction.structuredFormat;
          
          // Extract terms from structured format or fallback to text
          let terms: any[] = [];
          if (structuredFormat?.mainText) {
            terms.push({ value: structuredFormat.mainText.text || '' });
          }
          if (structuredFormat?.secondaryText) {
            terms.push({ value: structuredFormat.secondaryText.text || '' });
          }
          
          // If no structured format, split description by commas
          if (terms.length === 0 && text) {
            terms = text.split(',').map((t: string) => ({ value: t.trim() }));
          }

          return {
            place_id: placeId,
            description: text,
            terms: terms,
          };
        });

      processPredictions(results);
      setOpen(true);
      setActiveIndex(-1);
    } catch (error: any) {
      setLoading(false);
      console.error("Error fetching predictions:", error);
      setPredictions([]);
      setOpen(false);
    }
  };

  // -------------------------
  // Process and sort predictions
  // -------------------------
  const processPredictions = (predictions: any[]) => {
    let sortedPredictions = [...predictions];
    
    if (countryBias) {
      const countryCode = countryBias.toLowerCase();
      const countryName = countryCode === 'nz' || countryCode === 'nzl' ? 'new zealand' : null;
      
      console.log(`🔍 Sorting ${predictions.length} predictions for country: ${countryCode}`);
      
      // Sort: country matches first, then others
      sortedPredictions.sort((a, b) => {
        // Check last term (usually country)
        const aLastTerm = a.terms?.[a.terms.length - 1]?.value?.toLowerCase() || '';
        const bLastTerm = b.terms?.[b.terms.length - 1]?.value?.toLowerCase() || '';
        
        // Check description for country name or code
        const aDesc = a.description?.toLowerCase() || '';
        const bDesc = b.description?.toLowerCase() || '';
        
        const aIsCountry = aLastTerm.includes(countryCode) || 
                          aDesc.includes(countryCode) ||
                          (countryName && (aDesc.includes(countryName) || aLastTerm.includes(countryName)));
        
        const bIsCountry = bLastTerm.includes(countryCode) || 
                          bDesc.includes(countryCode) ||
                          (countryName && (bDesc.includes(countryName) || bLastTerm.includes(countryName)));
        
        if (aIsCountry && !bIsCountry) return -1;
        if (!aIsCountry && bIsCountry) return 1;
        return 0;
      });
      
      const nzCount = sortedPredictions.filter(p => {
        const desc = p.description?.toLowerCase() || '';
        const lastTerm = p.terms?.[p.terms.length - 1]?.value?.toLowerCase() || '';
        return desc.includes('new zealand') || desc.includes(', nz') || lastTerm.includes('nz');
      }).length;
      
      console.log(`✅ Found ${nzCount} NZ results, sorted to top`);
    }
    
    setPredictions(sortedPredictions);
    setOpen(true);
  };

  // -------------------------
  // Fetch full place details
  // Uses Place (new Google Maps Places API)
  // -------------------------
  const getDetails = async (placeId: string) => {
    if (!window.google?.maps) {
      console.error("Google Maps API not loaded");
      return;
    }

    setLoading(true);

    try {
      // Load Places library using importLibrary
      const placesLibrary = await loadPlacesLibrary();
      const { Place } = placesLibrary;
      
      const place = new Place({ id: placeId });
      
      await place.fetchFields({
        fields: [
          "formattedAddress",
          "addressComponents",
          "location",
        ],
      });

      setLoading(false);

      // Access properties from the Place object
      const formatted = place.formattedAddress || '';
      const comps = place.addressComponents || [];
      const loc = place.location;

      // Extract lat/lng from location object
      let lat: number | undefined;
      let lng: number | undefined;
      
      if (loc) {
        // Location can be LatLng object or have lat/lng properties
        if (typeof loc.lat === 'function') {
          lat = loc.lat();
          lng = loc.lng();
        } else {
          lat = loc.lat;
          lng = loc.lng;
        }
      }

      const details: AddressDetails = {
        formatted,
        lat,
        lng,
      };

      // Process address components - new API uses different property names
      comps.forEach((c: any) => {
        const types = c.types || [];
        const longText = c.longText || c.long_name;
        const shortText = c.shortText || c.short_name;
        
        if (types.includes("street_number")) details.streetNumber = longText;
        if (types.includes("route")) details.streetName = longText;
        if (types.includes("locality")) details.city = longText;
        if (types.includes("administrative_area_level_1"))
          details.state = shortText || longText;
        if (types.includes("postal_code")) details.postalCode = longText;
        if (types.includes("country")) details.country = longText;
      });

      details.street = [details.streetNumber, details.streetName]
        .filter(Boolean)
        .join(" ");

      // Store location for map display
      if (lat && lng) {
        setSelectedLocation({ lat, lng });
      }

      onChange(formatted);
      onPlaceSelect?.(details);

      setPredictions([]);
      setOpen(false);
      setIsUserTyping(false);
    } catch (error: any) {
      setLoading(false);
      console.error("Failed to get place details:", error);
    }
  };
  
  // -------------------------
  // Geocode address to get coordinates
  // -------------------------
  const geocodeAddress = (address: string): Promise<{ lat: number; lng: number }> => {
    return new Promise((resolve, reject) => {
      if (!window.google?.maps) {
        reject(new Error("Google Maps not loaded"));
        return;
      }

      if (!geocoderRef.current) {
        geocoderRef.current = new window.google.maps.Geocoder();
      }

      // Build geocoding request with optional biases
      // Note: componentRestrictions is NOT supported by Geocoding API, only Autocomplete API
      const request: any = { address };

      // Add region bias (country code) if available
      if (countryBias) {
        request.region = countryBias.toLowerCase();
      }

      // Add bounds bias if location bias is set (helps prioritize results in area)
      if (locationBias) {
        const radiusDegrees = 0.45; // ~50km at equator
        const lat = locationBias.lat;
        const lng = locationBias.lng;
        try {
          request.bounds = new window.google.maps.LatLngBounds(
            new window.google.maps.LatLng(lat - radiusDegrees, lng - radiusDegrees),
            new window.google.maps.LatLng(lat + radiusDegrees, lng + radiusDegrees)
          );
        } catch (e) {
          // If bounds creation fails, just use region
          console.warn("Failed to create bounds, using region only:", e);
        }
      }

      geocoderRef.current.geocode(request, (results: any, status: any) => {
        if (status === "OK" && results && results[0]) {
          const location = results[0].geometry.location;
          resolve({
            lat: location.lat(),
            lng: location.lng(),
          });
        } else if (status === "ZERO_RESULTS") {
          // Try again without biases as fallback
          if (countryBias || locationBias) {
            console.log("Retrying geocoding without biases...");
            geocoderRef.current.geocode({ address }, (fallbackResults: any, fallbackStatus: any) => {
              if (fallbackStatus === "OK" && fallbackResults && fallbackResults[0]) {
                const location = fallbackResults[0].geometry.location;
                resolve({
                  lat: location.lat(),
                  lng: location.lng(),
                });
              } else {
                reject(new Error(`Geocoding failed: ${fallbackStatus || status}`));
              }
            });
          } else {
            reject(new Error(`No results found for address: ${address}`));
          }
        } else {
          reject(new Error(`Geocoding failed: ${status}`));
        }
      });
    });
  };

  // -------------------------
  // Sync stored coordinates from props
  // -------------------------
  useEffect(() => {
    if (lat && lng) {
      setSelectedLocation({ lat, lng });
    } else if (!value) {
      // Clear location if address is cleared
      setSelectedLocation(null);
    }
  }, [lat, lng, value]);

  // -------------------------
  // Close dropdown when value changes programmatically
  // -------------------------
  useEffect(() => {
    // If value changes but user is not typing, close dropdown
    if (!isUserTyping && value) {
      setPredictions([]);
      setOpen(false);
    }
  }, [value, isUserTyping]);

  // -------------------------
  // Handle map button click
  // -------------------------
  const handleMapClick = async () => {
    // Check stored coordinates first (from props or state)
    const coordinates = selectedLocation || (lat && lng ? { lat, lng } : null);
    
    if (coordinates) {
      console.log("Using stored coordinates:", coordinates);
      setSelectedLocation(coordinates);
      setShowMapModal(true);
      return;
    }

    // If there's an address, try to geocode it
    if (value) {
      console.log("No stored coordinates, geocoding address:", value);
      setLoading(true);
      try {
        const location = await geocodeAddress(value);
        console.log("Geocoding successful:", location);
        setSelectedLocation(location);
        setShowMapModal(true);
      } catch (error: any) {
        console.error("Failed to geocode address:", error);
        console.error("Address attempted:", value);
        console.error("Country bias:", countryBias);
        console.error("Location bias:", locationBias);
        
        // More helpful error message
        const errorMessage = error?.message || "Unknown error";
        if (errorMessage.includes("ZERO_RESULTS") || errorMessage.includes("No results")) {
          alert(`Unable to find location for: "${value}"\n\nThis might be because:\n- The address format is unclear\n- The address doesn't exist\n- Try selecting from autocomplete suggestions for better results`);
        } else {
          alert(`Unable to find location for: "${value}"\n\nError: ${errorMessage}\n\nPlease try selecting an address from the autocomplete suggestions.`);
        }
      } finally {
        setLoading(false);
      }
      return;
    }

    // No address - use locationBias if available, or show default map
    if (locationBias) {
      console.log("No address, using location bias:", locationBias);
      setSelectedLocation({ lat: locationBias.lat, lng: locationBias.lng });
      setShowMapModal(true);
    } else {
      // No address and no location bias - show message
      alert("Please enter an address first, or select one from the autocomplete suggestions.");
    }
  };

  // -------------------------
  // Initialize map in modal
  // -------------------------
  useEffect(() => {
    if (showMapModal && selectedLocation && mapRef.current && window.google?.maps) {
      // Clear existing map
      if (mapInstanceRef.current) {
        mapInstanceRef.current = null;
      }
      
      // Initialize map
      const map = new window.google.maps.Map(mapRef.current, {
        center: { lat: selectedLocation.lat, lng: selectedLocation.lng },
        zoom: 15,
        mapTypeControl: true,
        streetViewControl: true,
        fullscreenControl: true,
      });

      // Add marker
      new window.google.maps.Marker({
        position: { lat: selectedLocation.lat, lng: selectedLocation.lng },
        map: map,
        title: value || "Selected Location",
      });

      mapInstanceRef.current = map;
    }
  }, [showMapModal, selectedLocation, value]);

  // -------------------------
  // Key navigation
  // -------------------------
  const handleKeyDown = (e: any) => {
    if (!predictions.length) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % predictions.length);
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + predictions.length) % predictions.length);
    }

    if (e.key === "Enter") {
      e.preventDefault();
      if (activeIndex >= 0) {
        const p = predictions[activeIndex];
        if (p.place_id) {
          getDetails(p.place_id);
        }
      }
    }
    
    if (e.key === "Escape") {
      setPredictions([]);
      setOpen(false);
      setActiveIndex(-1);
    }
  };

  // -------------------------
  // Init Google script
  // -------------------------
  useEffect(() => {
    const init = async () => {
      const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
      
      if (!apiKey) {
        console.error("Missing REACT_APP_GOOGLE_MAPS_API_KEY");
        return;
      }
      
      try {
        await loadGoogle(apiKey);
      } catch (err) {
        console.error("Failed to load Google Maps API:", err);
      }
    };
    init();
  }, []);
  
  // -------------------------
  // Click outside to close
  // -------------------------
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        setOpen(false);
        setPredictions([]);
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [open]);

  const showClearButton = value && !disabled;
  const showMapButton = !disabled; // Always show map icon when not disabled
  // Calculate padding to prevent text overlap with icons
  const getPaddingRight = () => {
    if (showClearButton && showMapButton) return '5rem'; // Both icons: map (right-3) + clear (right-3) + spacing
    if (showClearButton || showMapButton) return '2.75rem'; // Single icon: icon width + padding
    if (loading) return '2.75rem'; // Loading spinner
    return undefined;
  };

  return (
    <>
      <div ref={containerRef} className="relative w-full">
        {/* INPUT */}
        <input
          ref={inputRef}
          id={id}
          name={name}
          disabled={disabled}
          autoFocus={autoFocus}
          required={required}
          autoComplete="new-password"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck="false"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={open}
          data-form-type="other"
          data-lpignore="true"
          data-1p-ignore="true"
          data-bwignore="true"
          data-autocomplete="off"
          value={value || ''}
          onChange={(e) => {
            const newValue = e.target.value;
            setIsUserTyping(true);
            onChange(newValue);
            fetchPredictions(newValue);
          }}
          onKeyDown={handleKeyDown}
          onBlur={(e) => {
            setIsUserTyping(false);
            if (onBlur) onBlur(e);
          }}
          placeholder={placeholder}
          className={className || `
            w-full px-3 py-2 rounded-md border
            bg-white dark:bg-gray-800
            text-gray-900 dark:text-gray-100
            border-gray-300 dark:border-gray-600
            focus:outline-none focus:ring-2 focus:ring-primary-500
            placeholder-gray-500 dark:placeholder-gray-400
            disabled:opacity-50 disabled:cursor-not-allowed
            text-sm
          `}
          style={{
            fontSize: '13.33px',
            paddingRight: getPaddingRight(),
          }}
        />
        
        {/* MAP BUTTON */}
        {showMapButton && (
          <button
            type="button"
            onClick={handleMapClick}
            disabled={loading}
            className={`absolute top-1/2 -translate-y-1/2 text-gray-400 hover:text-accent-600 dark:hover:text-accent-400 transition-colors focus:outline-none disabled:opacity-50 ${showClearButton ? 'right-12' : 'right-3'}`}
            aria-label="View location on map"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
          </button>
        )}
        
        {/* CLEAR BUTTON */}
        {showClearButton && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors focus:outline-none"
            aria-label="Clear address"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}
        
        {/* LOADING SPINNER */}
        {loading && !showClearButton && !showMapButton && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <div className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {/* DROPDOWN - Absolute positioning relative to container */}
        {open && predictions.length > 0 && (
          <ul
            ref={dropdownRef}
            className="
              absolute top-full left-0 right-0 z-50
              bg-white dark:bg-gray-800 
              border border-gray-300 dark:border-gray-600
              rounded-md shadow-lg max-h-60 overflow-auto
              mt-0.5
            "
          >
            {predictions.map((p, i) => (
              <li
                key={p.place_id}
                onMouseDown={() => getDetails(p.place_id)}
                onMouseEnter={() => setActiveIndex(i)}
                className={`
                  px-3 py-2 cursor-pointer 
                  text-gray-900 dark:text-gray-100
                  ${i === activeIndex 
                    ? "bg-primary-500 text-white" 
                    : "hover:bg-gray-100 dark:hover:bg-gray-700"}
                `}
              >
                {p.description}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* MAP MODAL */}
      {showMapModal && selectedLocation && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[2147483647] p-4"
          onClick={() => setShowMapModal(false)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* MODAL HEADER */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Location Map
              </h3>
              <button
                onClick={() => setShowMapModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                aria-label="Close map"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* ADDRESS TEXT */}
            {value && (
              <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                <p className="text-sm text-gray-600 dark:text-gray-400">{value}</p>
              </div>
            )}

            {/* MAP CONTAINER */}
            <div className="flex-1 p-4">
              <div
                ref={mapRef}
                className="w-full h-[500px] rounded-md"
                style={{ minHeight: "400px" }}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};
